import { useQuery } from "@tanstack/react-query";
import { type AxiosPromise } from "axios";
import { ButtonSecondary } from "components/Atoms/Button";
import { Loading } from "components/panels";
import moment from "moment";
import React from "react";
import Panel from "../../components/panels/panel";
import { newAppointment } from "../../utilities/coordinatorUtils";
import AppointmentTypeFilter from "./AppointmentTypeFilter";
import { fireLinkedTravelUpdated, type LinkType, onLinkToggled } from "./DOMEvents";
import Itinerary from "./Itinerary";
import PhaseFilter from "./PhaseFilter";
import { getId } from "./utils";

interface Props {
  showId: number
  artist: server.dto.ArtistSmall
  showStartDate: moment.Moment
  showEndDate: moment.Moment

  disableNewAppointments: boolean
  canLinkUnlinkTravel: boolean

  panelConfig: server.dto.ItinerarySidebarPanelConfig
}

const itineraryTypesKey = "itinerary_types";
const itineraryPhasesKey = "itinerary_phases";

const getFromLocalStorage = (key: string): string[] => {
  const values = localStorage.getItem(key);
  if (values) {
    return values.split(",");
  }
  return undefined;
};

const setLocalStorage = (key: string, values: string[]) => {
  localStorage.setItem(key, values.join(","));
};

const defaultAppointmentTypes = serverReference.appointmentTypes.map(a => a.value);
const defaultPhases = serverReference.appointmentPhases.map(a => a.value);

export const itinerarySidebarQueryKey = "itinerarySidebar";

const ItinerarySidebar = (props: Props) => {
  const [startDate, setStartDate] = React.useState(moment.utc(props.showStartDate).subtract(2, "days"));
  const [endDate, setEndDate] = React.useState(moment.utc(props.showEndDate).add(3, "days"));
  const [phases, setPhases] = React.useState<server.dto.AppointmentPhases[]>(getFromLocalStorage(itineraryPhasesKey) as server.dto.AppointmentPhases[] ?? defaultPhases);
  const [appointmentTypes, setAppointmentTypes] = React.useState<server.dto.AppointmentTypes[]>(getFromLocalStorage(itineraryTypesKey) as server.dto.AppointmentTypes[] ?? defaultAppointmentTypes);

  const [linkedFlightSegmentIds, setLinkedFlightSegmentIds] = React.useState<number[]>(props.panelConfig.linkedFlightSegmentIds);
  const [linkedGroundTransportIds, setLinkedGroundTransportIds] = React.useState<number[]>(props.panelConfig.linkedGroundTransportIds);
  const [linkedTrainSegmentIds, setLinkedTrainSegmentIds] = React.useState<number[]>(props.panelConfig.linkedTrainSegmentIds);

  const instruction: server.dto.PlanningForItineraryQuery = {
    start: startDate.toISOString() as any,
    end: endDate.toISOString() as any,
    phases,
    appointmentTypes: appointmentTypes,
    artistIds: [props.artist.artistId],
  };

  const daysBetweenStartEnd = endDate.diff(startDate, "days");

  const api = useQuery({
    queryKey: [itinerarySidebarQueryKey, instruction],
    queryFn: async () => {
      return window.CoreApi.Planning.PlanningForItinerary(instruction);
    },
    keepPreviousData: true,
    enabled: daysBetweenStartEnd < serverReference.constants.appointment.maxDurationDays
  });
  const items = api.data?.data.data ?? [];

  const loadPreviousAppointments = () => {
    const newStart = startDate.clone().subtract(2, "days");
    setStartDate(newStart);
  };

  const loadNextAppointments = () => {
    const newEnd = endDate.clone().add(3, "days");
    setEndDate(newEnd);
  };

  const onAppointmentToggled = (type: server.dto.AppointmentTypes) => {
    const doesTypeAlreadyExist = appointmentTypes.includes(type);
    let newTypes: server.dto.AppointmentTypes[];
    if (doesTypeAlreadyExist) {
      newTypes = appointmentTypes.filter(t => t !== type);
    } else {
      newTypes = [...appointmentTypes, type];
    }

    if (newTypes.length === 0) {
      newTypes = serverReference.appointmentTypes.map(a => a.value).filter(a => a !== "unknown");
    }
    setAppointmentTypes(newTypes);
    setLocalStorage(itineraryTypesKey, newTypes);
  };

  const onPhaseToggled = (phase: server.dto.AppointmentPhases) => {
    const doesPhaseAlreadyExist = phases.includes(phase);
    let newPhases: server.dto.AppointmentPhases[];
    if (doesPhaseAlreadyExist) {
      newPhases = phases.filter(p => p !== phase);
    } else {
      newPhases = [...phases, phase];
    }
    if (newPhases.length === 0) {
      newPhases = serverReference.appointmentPhases.map(a => a.value).filter(a => a !== "unknown");
    }
    setPhases(newPhases);
    setLocalStorage(itineraryPhasesKey, newPhases);
  };

  const toggleLinked = async (appointment: server.dto.PlannerAppointment) => {
    const id = getId(appointment);
    const allLinkedIds = [...linkedFlightSegmentIds, ...linkedGroundTransportIds, ...linkedTrainSegmentIds];

    const isAlreadyLinked = allLinkedIds.includes(+id);

    let setIds: (value: React.SetStateAction<number[]>) => void;
    let apiEndpoint: (showId: number, linkedItemId: number) => AxiosPromise<server.dto.ApiResponse>;

    switch (appointment.type.value) {
      case "flight":
        setIds = setLinkedFlightSegmentIds;
        if (isAlreadyLinked) apiEndpoint = window.Api.Shows.UnlinkFlightSegment;
        else apiEndpoint = window.Api.Shows.LinkFlightSegment;
        break;
      case "groundTransport":
        setIds = setLinkedGroundTransportIds;
        if (isAlreadyLinked) apiEndpoint = window.Api.Shows.UnlinkGroundTransport;
        else apiEndpoint = window.Api.Shows.LinkGroundTransport;
        break;
      case "train":
        setIds = setLinkedTrainSegmentIds;
        if (isAlreadyLinked) apiEndpoint = window.Api.Shows.UnlinkTrainSegment;
        else apiEndpoint = window.Api.Shows.LinkTrainSegment;
        break;
      default:
        throw new Error(appointment.type.value);
    }

    if (isAlreadyLinked) {
      setIds(oldIds => oldIds.filter(oldId => oldId !== +id));
    } else {
      setIds(oldIds => [...oldIds, +id]);
    }

    await apiEndpoint(props.showId, +id);
    const linkedTravelRes = await window.Api.Shows.GetLinkedTravel(props.showId);
    fireLinkedTravelUpdated(linkedTravelRes.data);

    setLinkedFlightSegmentIds(linkedTravelRes.data.flights.map(s => s.flightSegmentId));
    setLinkedGroundTransportIds(linkedTravelRes.data.groundTransport.map(s => s.groundTransportId));
    setLinkedTrainSegmentIds(linkedTravelRes.data.trains.map(s => s.trainSegmentId));
  };

  const onLinkToggle = (linkType: LinkType, id: number) => {
    let setter: React.Dispatch<React.SetStateAction<number[]>>;
    switch (linkType) {
      case "flight": setter = setLinkedFlightSegmentIds; break;
      case "groundTransport": setter = setLinkedGroundTransportIds; break;
      case "train": setter = setLinkedTrainSegmentIds; break;
    }
    setter(oldValues => {
      if (oldValues.includes(id)) {
        return oldValues.filter(oldId => oldId !== id);
      } else {
        return [...oldValues, id];
      }
    });
  };

  React.useEffect(() => {
    const removeLinkToggleEventHandler = onLinkToggled(onLinkToggle);
    return removeLinkToggleEventHandler;
  }, []);

  React.useEffect(() => {
    setStartDate(moment.utc(props.showStartDate).subtract(2, "days"));
    setEndDate(moment.utc(props.showEndDate).add(3, "days"));
  }, [props.showEndDate, props.showStartDate]);

  const typeFilterMap = new Set(appointmentTypes);
  const phaseFilterMap = new Set(phases);

  const onNewButtonPressed = () => {
    newAppointment({
      artist: props.artist,
      date: startDate.toISOString(),
      fromItem: {
        type: "show",
        id: props.showId
      }
    });
  };

  const canAddNewAppointments = props.panelConfig.canAdd && !props.disableNewAppointments;

  return <Panel
    panelClassName="s1_related-feature-panel"
    title={props.artist.name}
    contentCssClass="flex-column gap-larger"
    button={canAddNewAppointments && <ButtonSecondary onClick={onNewButtonPressed} label={RESX.Buttons.btnNew} />}
  >

    <div style={{ display: "table", width: "100%" }} className="margin-top-larger">
      <div style={{ display: "table-cell" }}>
        <AppointmentTypeFilter type="show" isToggled={typeFilterMap.has("show")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="flight" isToggled={typeFilterMap.has("flight")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="train" isToggled={typeFilterMap.has("train")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="groundTransport" isToggled={typeFilterMap.has("groundTransport")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="accommodation" isToggled={typeFilterMap.has("accommodation")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="externalCalendar" isToggled={typeFilterMap.has("externalCalendar")} onToggled={onAppointmentToggled} />
      </div>
      <div style={{ display: "table-cell", textAlign: "right" }}>
        <PhaseFilter phase={"option"} isToggled={phaseFilterMap.has("option")} onToggled={onPhaseToggled} />
        <PhaseFilter phase={"confirmed"} isToggled={phaseFilterMap.has("confirmed")} onToggled={onPhaseToggled} />
        <PhaseFilter phase={"cancelled"} isToggled={phaseFilterMap.has("cancelled")} onToggled={onPhaseToggled} />
      </div>
    </div>
    <div>
      <div className="s1_morebutton s1_previous" onClick={loadPreviousAppointments}>
        <i className="icon-angle-up"></i>
      </div>
      {items.length > 0 && <Itinerary
        artist={props.artist}
        appointments={items}
        startMoment={startDate}
        endMoment={endDate}
        showId={props.showId}
        linkedFlightSegmentIds={linkedFlightSegmentIds}
        linkedGroundTransportIds={linkedGroundTransportIds}
        linkedTrainSegmentIds={linkedTrainSegmentIds}
        toggleLinked={toggleLinked}
        canCreateNewAppointment={canAddNewAppointments}
        canLinkUnlinkTravel={props.canLinkUnlinkTravel}
      />}
      {api.isInitialLoading && <Loading height={50} />}
      <div className="s1_morebutton s1_next" onClick={loadNextAppointments}>
        <i className="icon-angle-down"></i>
      </div>
    </div>
  </Panel>;
};

export default ItinerarySidebar;